import { useField } from '@hooks/useField';
import { IconButton, makeStyles, MenuItem, TableCell, TablePagination, TableRow, TextField, useTheme } from '@material-ui/core';
import { TablePaginationActionsProps } from '@material-ui/core/TablePagination/TablePaginationActions';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import LastPageIcon from '@material-ui/icons/LastPage';
import React from 'react';
import { IPagination } from '../../Hooks/usePagination';
import { GridItem } from '../GridItem';
import { HorizontalGrid } from '../HorizontalGrid';

export type PaginationProps = React.PropsWithChildren<{
    pagination: IPagination
}>

export function Pagination ({ children, pagination }: PaginationProps) {
    return (
        <TableRow>
            <TableCell colSpan={1000}>
                <HorizontalGrid verticalAlign='center'>
                    <GridItem>
                        { children }
                    </GridItem>

                    <GridItem floatRight>
                        <TablePagination 
                            component="div"
                            rowsPerPageOptions={[50]}
                            page={pagination.state.currentPage}
                            rowsPerPage={pagination.state.rowsPerPage}
                            count={pagination.state.totalCount}
                            onPageChange={pagination.handlePageChange}
                            ActionsComponent={TablePaginationActions}
                            style={{borderBottomWidth: 0}}
                        />
                    </GridItem>
                </HorizontalGrid>
            </TableCell>
        </TableRow>
    );
}

function TablePaginationActions({count, page, rowsPerPage, onPageChange}: TablePaginationActionsProps) {
    const classes = useStyles();
    const theme = useTheme();
    const pageNumberField = useField(page);
    const lastPage = React.useMemo(() => Math.max(0, Math.ceil(count / rowsPerPage) - 1), [count, rowsPerPage]);
  
    const handleFirstPageButtonClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, 0);
    }, [onPageChange]);
  
    const handleBackButtonClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    }, [onPageChange, page]);
  
    const handleNextButtonClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    }, [onPageChange, page]);
  
    const handleLastPageButtonClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, lastPage);
    }, [lastPage, onPageChange]);

    const handlePageNumberChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);

        if (!!value) {
            pageNumberField.setValue(0);
        }

        if (value !== pageNumberField.value && value >= 0 && value <= (lastPage + 1) && value !== page) {
            pageNumberField.setValue(value);
            onPageChange({} as React.MouseEvent<HTMLButtonElement, MouseEvent>, value);
        }
    }, [lastPage, onPageChange, page, pageNumberField])

    React.useEffect(() => {
        if (pageNumberField.value !== page) {
            pageNumberField.setValue(page)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    return (
        <div className={classes.root}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            
            <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
                {theme.direction === 'rtl' ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
            </IconButton>

            <TextField
                value={pageNumberField.value}
                onChange={handlePageNumberChange}
                className={classes.pageNumberField}
                select
                variant="outlined"
                style={{
                    marginTop: 6
                }}
                SelectProps={{
                    SelectDisplayProps: {
                        style: {
                            padding: 8
                        }
                    }
                }}
                inputProps={{
                    style: {
                        textAlign: 'center',
                    }
                }}
            >
                {
                    Array.from(Array(lastPage + 1).keys()).map(i => {
                        return <MenuItem key={i} value={i}>{i + 1}</MenuItem>
                    })
                }
            </TextField>

            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
            
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </div>
    );
}

const useStyles = makeStyles(theme => ({
    root: {
        flexShrink: 0,
        marginLeft: theme.spacing(2.5),
    },

    pageNumberField: {
        width: 65,
    }
}));